import React, { FunctionComponent } from 'react'
import { T } from '@transifex/react'
import { ErrorMessage, Link } from '@lightspeed/design-system-react'
import { PROMO_CODE_STATUES } from '../../../promotions/promotions.constant'

const CheckPromotionLink: FunctionComponent<{ onClick: () => void }> = ({
  onClick,
}) => (
  <Link data-track="check-promotion" href="#" onClick={onClick} isSecondary>
    <T _str="Check promotion." />
  </Link>
)

const InsufficientGetErrorMessage: FunctionComponent<{
  onClick: () => void
}> = ({ onClick }) => (
  <>
    <T _str="Add a " />
    <Link
      data-track="redeemable-product"
      href="#"
      onClick={onClick}
      isSecondary
    >
      <T _str="redeemable product" />
    </Link>
    <T _str=" first to use this code." />
  </>
)

const InsufficientSpendErrorMessage: FunctionComponent<{
  onClick: () => void
}> = ({ onClick }) => (
  <>
    <Link
      data-track="see-what-required-to-redeem-this-code"
      href="#"
      onClick={onClick}
      isSecondary
    >
      <T _str="See what's required to redeem this code." />
    </Link>
  </>
)

export function getTranslatedPromoCodeErrorMessage(
  promoCodeStatus: string | null
) {
  switch (promoCodeStatus) {
    case PROMO_CODE_STATUES.EXPIRED:
      return (
        <T
          _str="This code can no longer be redeemed."
          _context="Expired promo code error message"
        />
      )
    case PROMO_CODE_STATUES.INCORRECT_OUTLET:
      return (
        <T
          _str="This code can’t be redeemed at this outlet."
          _context="Incorrect outlet promo code error message"
        />
      )
    case PROMO_CODE_STATUES.INSUFFICIENT_BUY:
      return (
        <T
          _str="This code can’t be redeemed because the buy condition hasn’t been met."
          _context="Insufficient buy promo code error message"
        />
      )
    case PROMO_CODE_STATUES.INSUFFICIENT_SPEND:
      return (
        <T
          _str="This code can’t be redeemed because the spend condition hasn’t been met."
          _context="Insufficient spend promo code error message"
        />
      )
    case PROMO_CODE_STATUES.INSUFFICIENT_GET:
      return (
        <T
          _str="This code can’t be redeemed because the get condition hasn’t been met. "
          _context="Insufficient get promo code error message"
        />
      )
    case PROMO_CODE_STATUES.INVALID:
      return (
        <T
          _str="This code is invalid."
          _context="Invalid promo code error message"
        />
      )
    case PROMO_CODE_STATUES.OTHER:
      return (
        <T
          _str="This code can’t be redeemed because it conflicts with other discounts/promotions already applied."
          _context="Other promo code error message"
        />
      )
    case PROMO_CODE_STATUES.REDEEMED:
      return (
        <T
          _str="This code has already been redeemed."
          _context="Already redeemed promo code error message"
        />
      )
    case PROMO_CODE_STATUES.REDEEMED_MULTI:
      return (
        <T
          _str="This code has run out of redemptions."
          _context="All redemptions used promo code error message"
        />
      )
    case PROMO_CODE_STATUES.UPCOMING:
      return (
        <T
          _str="This code can’t be redeemed yet."
          _context="Upcoming promo code error message"
        />
      )
    default:
      return (
        <T
          _str="This code couldn’t be validated. Refresh the page and try again."
          _context="Default promo code error message"
        />
      )
  }
}

type Props = {
  promoCodeStatus: string | null
  openPromoModal: () => void
}

// Error message for Add promo code popover validation
export const PromoCodeError: FunctionComponent<Props> = ({
  promoCodeStatus,
  openPromoModal,
}) => {
  const shouldShowPromoCodeLink =
    promoCodeStatus === PROMO_CODE_STATUES.EXPIRED ||
    promoCodeStatus === PROMO_CODE_STATUES.INCORRECT_OUTLET ||
    promoCodeStatus === PROMO_CODE_STATUES.UPCOMING ||
    promoCodeStatus === PROMO_CODE_STATUES.INSUFFICIENT_BUY

  return (
    <ErrorMessage>
      {getTranslatedPromoCodeErrorMessage(promoCodeStatus)}
      {shouldShowPromoCodeLink && (
        <>
          {' '}
          <CheckPromotionLink onClick={openPromoModal} />
        </>
      )}
      {promoCodeStatus === PROMO_CODE_STATUES.INSUFFICIENT_GET && (
        <>
          {' '}
          <InsufficientGetErrorMessage onClick={openPromoModal} />
        </>
      )}
      {promoCodeStatus === PROMO_CODE_STATUES.INSUFFICIENT_SPEND && (
        <>
          {' '}
          <InsufficientSpendErrorMessage onClick={openPromoModal} />
        </>
      )}
    </ErrorMessage>
  )
}
